import { Enrollment, Field, FieldGeometry, ImageryExtent } from "@/types/field";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
    ADVANCED_COMPLETE_OPTIONS,
    getImageryTypeOptions,
    Imagery,
    ImageryType,
    IRRIGATION_OPTIONS,
} from "@/types/imagery";
import { CropCycleV2 } from "@/types/cropCycle";
import dayjs from "dayjs";
import FieldDashboardContext, {
    InsightsQueryParams,
} from "./FieldDashboardContext";
import useRequest from "../request/useRequest";
import {
    Coordinates,
    invertedTurfBboxToFourCornerCoordinates,
} from "../utils/map";
import { bbox } from "@turf/turf";
import { EnrollmentTier } from "../utils/enrollment";
import useFeatureFlag from "../featureFlags/useFeatureFlags";
import useAuth from "../auth/useAuth";
import { Resource } from "@/types/user";
import { NaveWaterEstimate } from "@/types/water";
import { ManagementZones } from "@/types/managementZones";
import { isFieldTrial } from "../utils/access";
import { getTodayWithTimeTravel } from "../utils/date";
import { Status } from "@/types/enums";
import {
    findCropCycleBySearchId,
    getCropCycleSearchId,
} from "../utils/cropCycles";

function FieldDashboardProvider({ children }: { children: ReactNode }) {
    const { useGetRequest } = useRequest();
    const {
        irrigationEnabled,
        fieldIrrigationOn,
        organizationPreviewEnabled,
    } = useFeatureFlag();
    const { isAtLeastSentinel, user, isConsultantOrSentinel } = useAuth();
    const navigate = useNavigate();
    const { fieldId } = useParams({
        strict: false,
    });
    const { crop_cycle: cropCycleSearch, crop_zone: cropZoneSearch } =
        useSearch({
            strict: false,
        }) as { crop_cycle?: number | string; crop_zone?: number | string };
    const cropCycleSearchId =
        cropCycleSearch != null && cropCycleSearch !== ""
            ? Number(cropCycleSearch)
            : undefined;
    const cropZoneSearchId =
        cropZoneSearch != null && cropZoneSearch !== ""
            ? Number(cropZoneSearch)
            : undefined;
    const fieldQuery = useGetRequest<Field>({
        url: `fields/${fieldId}/`,
        enabled: !!fieldId,
    });
    const [selectedImage, setSelectedImage] = useState<Imagery | undefined>();

    const geometryQuery = useGetRequest<FieldGeometry>({
        url: `fields/${fieldId}/geometry/`,
        enabled: !!fieldId,
    });

    const cropCycleQueryV2 = useGetRequest<CropCycleV2[]>({
        url: `fields/${fieldId}/crop_cycles_v2/`,
        enabled: !!fieldId,
    });

    const cropZones = useMemo(() => {
        return fieldQuery?.data?.crop_zones ?? [];
    }, [fieldQuery?.data?.crop_zones]);

    const cropCycles = cropCycleQueryV2?.data;

    const selectedCropCycle = useMemo(() => {
        if (
            !cropCycles?.length ||
            cropCycleSearchId == null ||
            Number.isNaN(cropCycleSearchId)
        ) {
            return null;
        }
        return findCropCycleBySearchId(cropCycles, cropCycleSearchId);
    }, [cropCycles, cropCycleSearchId]);

    const setSelectedCropCycle = useCallback(
        (cycle: CropCycleV2) => {
            const nextId = getCropCycleSearchId(cycle);
            if (nextId === cropCycleSearchId) return;
            navigate({
                to: ".",
                search: (searchPrev) => ({
                    ...searchPrev,
                    crop_cycle: nextId,
                }),
                replace: true,
            });
        },
        [cropCycleSearchId, navigate],
    );

    useEffect(() => {
        if (!fieldId || !cropCycles?.length) return;
        if (
            cropCycleSearchId != null &&
            !Number.isNaN(cropCycleSearchId) &&
            findCropCycleBySearchId(cropCycles, cropCycleSearchId)
        ) {
            return;
        }

        const isActiveZoneCycle = (cycle: CropCycleV2) => {
            const zone = cropZones.find((z) => z.id === cycle.crop_zone);
            return zone?.status !== Status.Inactive;
        };
        const preferActiveZoneCycles = (cycles: CropCycleV2[]) => {
            const active = cycles.filter(isActiveZoneCycle);
            return active.length > 0 ? active : cycles;
        };
        const preferRequestedZoneCycles = (cycles: CropCycleV2[]) => {
            if (
                cropZoneSearchId == null ||
                Number.isNaN(cropZoneSearchId)
            ) {
                return cycles;
            }
            const forZone = cycles.filter(
                (cycle) => cycle.crop_zone === cropZoneSearchId,
            );
            return forZone.length > 0 ? forZone : cycles;
        };

        const scopedCycles = preferRequestedZoneCycles(cropCycles);
        const cyclesWithPlantingInPast = preferActiveZoneCycles(
            scopedCycles.filter((cycle) =>
                dayjs(cycle.effective_planting_operation.start_date).isBefore(
                    getTodayWithTimeTravel(),
                ),
            ),
        );
        const candidates =
            cyclesWithPlantingInPast.length > 0
                ? cyclesWithPlantingInPast
                : preferActiveZoneCycles(scopedCycles);
        const defaultCycle = candidates[candidates.length - 1];
        if (!defaultCycle) return;

        navigate({
            to: ".",
            search: (prev) => ({
                ...prev,
                crop_cycle: getCropCycleSearchId(defaultCycle),
            }),
            replace: true,
        });
    }, [
        fieldId,
        cropCycles,
        cropCycleSearchId,
        cropZoneSearchId,
        cropZones,
        navigate,
    ]);

    const selectedCropZone = useMemo(() => {
        if (!cropZones.length) return null;
        if (selectedCropCycle?.crop_zone != null) {
            const fromCycle = cropZones.find(
                (zone) => zone.id === selectedCropCycle.crop_zone,
            );
            if (fromCycle) return fromCycle;
        }
        return (
            cropZones.find((zone) => zone.status !== Status.Inactive) ??
            cropZones[0]
        );
    }, [cropZones, selectedCropCycle?.crop_zone]);

    const fieldImageryQuery = useGetRequest<Imagery[]>({
        url: `fields/${fieldId}/imagery/`,
        enabled: !!fieldId && !!selectedCropCycle,
    });

    const fieldEnrollmentQuery = useGetRequest<Enrollment[]>({
        url: `fields/${fieldId}/enrollments/`,
        enabled: !!fieldId,
    });

    const [selectedDate, setSelectedDate] = useState<string | undefined>();
    const [imageryVisible, setImageryVisible] = useState<boolean>(true);

    const imageryOptions = useMemo(() => {
        if (!selectedDate) return [];
        const imageDate = dayjs(selectedDate);
        const enrollmentForDate = fieldEnrollmentQuery?.data?.find(
            (enrollment) =>
                dayjs(enrollment.start_date).isSameOrBefore(imageDate) &&
                dayjs(enrollment.end_date).isSameOrAfter(imageDate),
        );

        // If we don't have an enrollment but have a date, it's from pre-2025 enrollment work, so return everything
        if (!enrollmentForDate) {
            if (irrigationEnabled && isAtLeastSentinel(Resource.IRRIGATION)) {
                return [...ADVANCED_COMPLETE_OPTIONS, ...IRRIGATION_OPTIONS];
            }
            return ADVANCED_COMPLETE_OPTIONS;
        }
        const imageryOptions = getImageryTypeOptions(
            enrollmentForDate?.enrollment_tier as EnrollmentTier,
        );
        if (irrigationEnabled && isAtLeastSentinel(Resource.IRRIGATION)) {
            return [...imageryOptions, ...IRRIGATION_OPTIONS];
        }
        return imageryOptions;
    }, [
        fieldEnrollmentQuery?.data,
        irrigationEnabled,
        isAtLeastSentinel,
        selectedDate,
    ]);

    const filteredImagery = useMemo(() => {
        const mostRecentImagesMap: Map<string, Imagery> = new Map<
            string,
            Imagery
        >();
        fieldImageryQuery?.data?.forEach((image: Imagery) => {
            const imageCaptureDateTime = dayjs.utc(image.capture_date).local();
            const imageCaptureDateString =
                imageCaptureDateTime.format("YYYY-MM-DD");

            const existingImage = mostRecentImagesMap.get(
                imageCaptureDateString,
            );

            if (image.active_for_day || !existingImage) {
                mostRecentImagesMap.set(imageCaptureDateString, image);
            }
        });

        return Array.from(mostRecentImagesMap.values());
    }, [fieldImageryQuery?.data]);

    const fieldBbox = useMemo(() => {
        if (!fieldQuery?.data || !geometryQuery?.data) return;

        return invertedTurfBboxToFourCornerCoordinates(
            bbox(geometryQuery.data.field),
        ) as Coordinates;
    }, [fieldQuery?.data, geometryQuery?.data]);

    const imageryBbox = useMemo(() => {
        if (!fieldQuery?.data || !geometryQuery?.data) return;

        if (
            fieldQuery.data.imagery_extent === ImageryExtent.FIELD ||
            !geometryQuery.data.irrigation_systems.features?.length
        ) {
            return fieldBbox;
        } else {
            return invertedTurfBboxToFourCornerCoordinates(
                bbox(geometryQuery.data?.irrigation_systems.features[0]),
            );
        }
    }, [fieldQuery?.data, geometryQuery?.data, fieldBbox]) as
        | Coordinates
        | undefined;

    const fieldIsAdvanced = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;

        return [
            EnrollmentTier.Advanced,
            EnrollmentTier.Basic,
            EnrollmentTier.AdvancedV2,
        ].includes(
            fieldQuery?.data?.active_enrollment
                ?.enrollment_tier as EnrollmentTier,
        );
    }, [fieldQuery?.data]);

    const fieldIsStandard = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            fieldQuery?.data?.active_enrollment?.enrollment_tier ===
            EnrollmentTier.Standard
        );
    }, [fieldQuery?.data]);

    const fieldIsComplete = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            fieldQuery?.data?.active_enrollment?.enrollment_tier ===
            EnrollmentTier.Complete
        );
    }, [fieldQuery?.data]);

    const fieldIsMonitoring = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            fieldQuery?.data?.active_enrollment?.enrollment_tier ===
            EnrollmentTier.Monitoring
        );
    }, [fieldQuery?.data]);

    const fieldIsPreview = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            fieldQuery?.data?.active_enrollment?.enrollment_tier ===
                EnrollmentTier.Preview &&
            !isConsultantOrSentinel &&
            !organizationPreviewEnabled
        );
    }, [fieldQuery?.data, isConsultantOrSentinel, organizationPreviewEnabled]);

    const fieldHasIrrigation = useMemo(() => {
        if (fieldId && fieldIrrigationOn(Number(fieldId))) return true;
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            [EnrollmentTier.Irrigation, EnrollmentTier.Complete].includes(
                fieldQuery?.data?.active_enrollment
                    ?.enrollment_tier as EnrollmentTier,
            ) && irrigationEnabled
        );
    }, [
        fieldIrrigationOn,
        fieldQuery?.data?.active_enrollment,
        fieldId,
        irrigationEnabled,
    ]);

    const fieldWaterQuery = useGetRequest<NaveWaterEstimate[]>({
        url: `fields/${fieldId}/water/`,
        enabled: !!fieldId && fieldHasIrrigation,
        params: {
            crop_zone_id: selectedCropZone?.id,
        },
    });

    const insightsParams = useMemo((): InsightsQueryParams => {
        return {
            crop_zone_id: selectedCropZone?.id,
            crop_cycle_id: selectedCropCycle?.id ?? undefined,
            start_date: selectedCropCycle?.start_date,
            end_date: selectedCropCycle?.end_date,
        };
    }, [
        selectedCropZone?.id,
        selectedCropCycle?.id,
        selectedCropCycle?.start_date,
        selectedCropCycle?.end_date,
    ]);

    const [imageType, setImageType] = useState<ImageryType>(ImageryType.NDRE);

    const managementZonesQuery = useGetRequest<ManagementZones[]>({
        url: `fields/${fieldId}/management_zones/`,
        enabled: !!fieldId,
    });

    const fieldIsTrial = useMemo(() => {
        if (!fieldQuery?.data || !user) return false;
        return isFieldTrial(fieldQuery?.data, user);
    }, [fieldQuery?.data, user]);

    return (
        <FieldDashboardContext.Provider
            value={{
                fieldId,
                cropCycleQueryV2,
                selectedCropCycle,
                setSelectedCropCycle,
                cropZones,
                selectedCropZone,
                insightsParams,
                fieldImageryQuery,
                fieldQuery,
                filteredImagery,
                setSelectedImage,
                selectedImage,
                geometryQuery,
                imageryBbox,
                fieldIsAdvanced,
                fieldIsStandard,
                fieldIsComplete,
                imageType,
                setImageType,
                fieldBbox,
                fieldEnrollmentQuery,
                imageryOptions,
                fieldHasIrrigation,
                fieldWaterQuery,
                selectedDate,
                setSelectedDate,
                managementZonesQuery,
                imageryVisible,
                setImageryVisible,
                fieldIsTrial,
                fieldIsMonitoring,
                fieldIsPreview,
            }}
        >
            {children}
        </FieldDashboardContext.Provider>
    );
}

export default FieldDashboardProvider;
